import React, { useState, useContext, useEffect, useRef } from "react";
import {
    Box,
    VStack,
    Text,
    Flex,
    Input,
    Spinner,
    Divider,
    IconButton,
    HStack,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    FormControl,
    FormLabel,
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    useDisclosure,
} from "@chakra-ui/react";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import moment from "moment";
import ShadowBox from "components/common/ShadowBox/ShadowBox";

const EmergingRegion = () => {
    const { toastNotify } = useContext(AppContext);
    const [regions, setRegions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    
    // Form States
    const [region, setRegion] = useState("");
    const [companyCode, setCompanyCode] = useState("");
    const [editingRegion, setEditingRegion] = useState<any>(null);

    // Delete Modal States
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [idToDelete, setIdToDelete] = useState<string | null>(null);
    const cancelRef = useRef<any>();

    const fetchRegions = async () => {
        try {
            setLoading(true);
            const response: any = await appRequest("emergingRegion", "getall", {});
            if (response?.status === "success") {
                setRegions(Array.isArray(response.data) ? response.data : []);
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to fetch emerging regions",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Fetch regions error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRegions();
    }, []);

    const handleAddRegion = async () => {
        if (!region.trim() || !companyCode.trim()) {
            toastNotify({
                title: "Validation Error",
                message: "Region and Company Code are required",
                type: "warning",
            });
            return;
        }

        try {
            setAddLoading(true);
            const response: any = await appRequest("emergingRegion", "create", {
                processName: "emerging",
                region: region.trim(),
                companyCode: companyCode.trim(),
            });

            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "Emerging region added successfully",
                    type: "success",
                });
                resetForm();
                fetchRegions();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to add region",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Add region error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    const handleUpdateRegion = async () => {
        if (!region.trim() || !companyCode.trim() || !editingRegion?._id) return;

        try {
            setAddLoading(true);
            const response: any = await appRequest("emergingRegion", "update", {
                id: editingRegion._id,
                region: region.trim(),
                companyCode: companyCode.trim(),
            });

            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "Emerging region updated successfully",
                    type: "success",
                });
                setEditingRegion(null);
                resetForm();
                fetchRegions();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to update region",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Update region error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    const confirmDelete = (id: string) => {
        setIdToDelete(id);
        onDeleteOpen();
    };

    const handleDeleteRegion = async () => {
        if (!idToDelete) return;
        try {
            const response: any = await appRequest("emergingRegion", "delete", { id: idToDelete });
            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "Region deleted successfully",
                    type: "success",
                });
                onDeleteClose();
                setIdToDelete(null);
                fetchRegions();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to delete region",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Delete region error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        }
    };

    const resetForm = () => {
        setRegion("");
        setCompanyCode("");
        setEditingRegion(null);
    };

    const startEdit = (reg: any) => {
        setEditingRegion(reg);
        setRegion(reg.region || "");
        setCompanyCode(reg.companyCode || "");
    };

    return (
        <ShadowBox>
            <VStack spacing={6} align="stretch" py={4}>
                <Flex justify="space-between" align="center">
                    <Box>
                        <Text fontSize="2xl" fontWeight="800" color="teal.500">
                            Emerging Regions
                        </Text>
                        <Text fontSize="xs" color="gray.500" fontWeight="500">
                            Manage regions and company codes for emerging processes
                        </Text>
                    </Box>
                </Flex>

                <Box bg="teal.50" p={5} borderRadius="xl" border="1px solid" borderColor="teal.100">
                    <Text fontSize="sm" fontWeight="700" mb={4} color="teal.700">
                        {editingRegion ? "Update Emerging Region" : "Add New Emerging Region"}
                    </Text>
                    <Flex direction={{ base: "column", md: "row" }} gap={4} align="flex-end">
                        <FormControl isRequired>
                            <FormLabel fontSize="xs">Region</FormLabel>
                            <Input
                                placeholder="e.g. Alaska"
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                bg="white"
                                borderRadius="lg"
                                fontSize="sm"
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontSize="xs">Company Code</FormLabel>
                            <Input
                                placeholder="e.g. 1000"
                                value={companyCode}
                                onChange={(e) => setCompanyCode(e.target.value)}
                                bg="white"
                                borderRadius="lg"
                                fontSize="sm"
                            />
                        </FormControl>
                        <HStack mt={{ base: 4, md: 0 }}>
                            <Button
                                colorScheme="teal"
                                onClick={editingRegion ? handleUpdateRegion : handleAddRegion}
                                isLoading={addLoading}
                                leftIcon={editingRegion ? <EditIcon /> : <AddIcon />}
                                borderRadius="lg"
                                px={8}
                                fontSize="sm"
                            >
                                {editingRegion ? "Update" : "Add"}
                            </Button>
                            {editingRegion && (
                                <Button size="sm" variant="ghost" onClick={resetForm}>
                                    Cancel
                                </Button>
                            )}
                        </HStack>
                    </Flex>
                </Box>

                <Divider />

                <Box>
                    <Text fontSize="sm" fontWeight="700" mb={4} color="gray.700">
                        Existing Emerging Regions
                    </Text>
                    {loading ? (
                        <Flex justify="center" align="center" py={10}>
                            <Spinner color="teal.500" />
                        </Flex>
                    ) : regions.length > 0 ? (
                        <TableContainer border="1px solid" borderColor="gray.100" borderRadius="xl" bg="gray.50">
                            <Table variant="simple" size="sm">
                                <Thead bg="gray.100">
                                    <Tr>
                                        <Th py={4}>Region</Th>
                                        <Th py={4}>Company Code</Th>
                                        <Th py={4}>Created By</Th>
                                        <Th py={4}>Created At</Th>
                                        <Th py={4} textAlign="right">Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {regions.map((reg) => (
                                        <Tr
                                            key={reg._id}
                                            _hover={{ bg: "white" }}
                                            transition="all 0.2s"
                                        >
                                            <Td>
                                                <HStack>
                                                    <Badge colorScheme="teal" variant="subtle" borderRadius="full" px={2}>
                                                        {reg.region?.charAt(0) || "R"}
                                                    </Badge>
                                                    <Text fontWeight="600">{reg.region}</Text>
                                                </HStack>
                                            </Td>
                                            <Td>
                                                <Text fontWeight="500" color="gray.700">{reg.companyCode}</Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="xs" color="gray.600">
                                                    {reg.createdBy?.name || reg.createdBy?.username || "System"}
                                                </Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="xs" color="gray.600">
                                                    {reg.created_At ? moment(reg.created_At).format("DD-MM-YYYY HH:mm") : "N/A"}
                                                </Text>
                                            </Td>
                                            <Td textAlign="right">
                                                <HStack spacing={1} justify="flex-end">
                                                    <IconButton
                                                        aria-label="Edit region"
                                                        icon={<EditIcon />}
                                                        size="sm"
                                                        variant="ghost"
                                                        colorScheme="blue"
                                                        onClick={() => startEdit(reg)}
                                                    />
                                                    <IconButton
                                                        aria-label="Delete region"
                                                        icon={<DeleteIcon />}
                                                        size="sm"
                                                        variant="ghost"
                                                        colorScheme="red"
                                                        onClick={() => confirmDelete(reg._id)}
                                                    />
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Flex direction="column" align="center" justify="center" py={10} color="gray.400">
                            <Text fontSize="sm">No regions found</Text>
                        </Flex>
                    )}
                </Box>

                {/* Delete Confirmation Modal */}
                <AlertDialog
                    isOpen={isDeleteOpen}
                    leastDestructiveRef={cancelRef}
                    onClose={onDeleteClose}
                    isCentered
                >
                    <AlertDialogOverlay>
                        <AlertDialogContent borderRadius="xl">
                            <AlertDialogHeader fontSize="lg" fontWeight="bold">
                                Delete Emerging Region
                            </AlertDialogHeader>

                            <AlertDialogBody>
                                Are you sure you want to delete this region? This action cannot be undone.
                            </AlertDialogBody>

                            <AlertDialogFooter>
                                <Button ref={cancelRef} onClick={onDeleteClose} borderRadius="lg">
                                    Cancel
                                </Button>
                                <Button colorScheme="red" onClick={handleDeleteRegion} ml={3} borderRadius="lg">
                                    Delete
                                </Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogOverlay>
                </AlertDialog>
            </VStack>
        </ShadowBox>
    );
};

export default EmergingRegion;
